import { useCallback, useEffect, useRef } from 'react';
import {
  usePageQuery,
  useSelectedCollectionQuery,
  useSelectedGroupQuery,
} from 'state/queryParams';

interface Pagination {
  page: number;
  nextPage: () => void;
  previousPage: () => void;
  setPageNumber: (pageNumber: number) => void;
  calcTotalPages: (countOfItems: number, itemsPerPage?: number) => number;
  resetPagination: () => void;
}

const DEFAULT_INITIAL_PAGE = 1;
const DEFAULT_ITEMS_PER_PAGE = 20;

export const useResetPagination = () => {
  const [, setPageQueryString] = usePageQuery();
  const resetPagination = () => setPageQueryString(DEFAULT_INITIAL_PAGE.toString());

  return { resetPagination };
};

export const usePaginate = (initialPage = DEFAULT_INITIAL_PAGE): Pagination => {
  const [pageQueryString, setPageQueryString] = usePageQuery();
  const [groupIdQuery] = useSelectedGroupQuery();
  const [selectedCollection] = useSelectedCollectionQuery();
  const { resetPagination } = useResetPagination();

  // Track previous values to detect actual changes
  const prevGroupIdRef = useRef(groupIdQuery);
  const prevCollectionRef = useRef(selectedCollection);

  useEffect(() => {
    if (!pageQueryString && initialPage) {
      setPageQueryString(initialPage.toString());
    }
  }, [pageQueryString]);

  useEffect(() => {
    // Only reset pagination if these values actually changed from a previous value
    // and aren't just being initialized
    if (
      (prevGroupIdRef.current !== groupIdQuery && prevGroupIdRef.current !== undefined) ||
      (prevCollectionRef.current !== selectedCollection &&
        prevCollectionRef.current !== undefined)
    ) {
      setPageQueryString(DEFAULT_INITIAL_PAGE.toString());
    }

    prevGroupIdRef.current = groupIdQuery;
    prevCollectionRef.current = selectedCollection;
  }, [groupIdQuery, selectedCollection, setPageQueryString]);

  const setPageNumber = (pageNumber: number) => {
    if (pageNumber === undefined || pageNumber < 1) {
      resetPagination();
    }
    setPageQueryString(pageNumber.toString());
  };

  const nextPage = () => {
    if (!pageQueryString) {
      resetPagination();
    } else {
      setPageNumber(Number(pageQueryString) + 1);
    }
  };

  const previousPage = () => {
    if (!pageQueryString) {
      resetPagination();
    } else {
      setPageNumber(Number(pageQueryString) - 1);
    }
  };

  const calcTotalPages = useCallback(
    (countOfItems: number, itemsPerPage: number = DEFAULT_ITEMS_PER_PAGE) => {
      return Math.ceil(countOfItems / itemsPerPage);
    },
    [DEFAULT_ITEMS_PER_PAGE],
  );

  return {
    page: Number(pageQueryString),
    nextPage,
    previousPage,
    setPageNumber,
    calcTotalPages,
    resetPagination,
  };
};
